import { msg } from "@lingui/core/macro";
import { type Component, type JSX, Show } from "solid-js";
import { CloudOff, Lock, SearchX } from "@fleet/icons";
import { useI18n } from "@/providers/i18n-provider";
import { TryAnotherAccount } from "@/features/auth/try-another-account";
import { EmptyState } from "./empty-state";
import { Button } from "./button";

type NotFoundVariant = "not-found" | "no-access" | "offline";

interface NotFoundStateProps {
  variant?: NotFoundVariant;
  /** Entity label (e.g. "project", "entry") to customize the copy. */
  entityLabel?: string;
  /** Custom title and description (override the default copy). */
  title?: string;
  description?: string;
  /** Primary action (e.g. return home / to the parent list). */
  primaryAction?: { label: string; onClick: () => void };
  /** Optional secondary action (e.g. retry). */
  secondaryAction?: { label: string; onClick: () => void };
  class?: string;
}

const ICONS: Record<NotFoundVariant, Component<{ class?: string }>> = {
  "not-found": SearchX,
  "no-access": Lock,
  offline: CloudOff,
};

/**
 * "Not found / no access / offline" state for detail routes
 * whose entity could not be resolved (see `createEntityResolution`). Reuses the
 * premium `EmptyState` (brand-aware illustration, animations behind a
 * reduced-motion guard) and offers localized copy by default + clear actions instead
 * of leaving the screen loading indefinitely.
 */
export const NotFoundState: Component<NotFoundStateProps> = (props) => {
  const { t } = useI18n();
  const variant = (): NotFoundVariant => props.variant ?? "not-found";

  const defaultTitle = (): string => {
    const titles: Record<NotFoundVariant, string> = {
      "no-access": t(msg`You don't have access`),
      offline: t(msg`Can't load this yet`),
      "not-found": t(msg`Not found`),
    };
    return titles[variant()];
  };

  const defaultDescription = (): string => {
    const descriptions: Record<NotFoundVariant, string> = {
      "no-access": t(msg`This item doesn't exist or you don't have permission to view it.`),
      offline: t(
        msg`You're offline and this hasn't synced to this device yet. Reconnect and try again.`,
      ),
      "not-found": t(msg`This item may have been deleted, or the link is no longer valid.`),
    };
    return descriptions[variant()];
  };

  const action = (): JSX.Element => (
    <div class="flex flex-col items-center gap-2 sm:flex-row">
      <Show when={props.primaryAction}>
        {(primary) => (
          <Button
            onClick={() => {
              primary().onClick();
            }}
          >
            {primary().label}
          </Button>
        )}
      </Show>
      <Show when={props.secondaryAction}>
        {(secondary) => (
          <Button
            variant="outline"
            onClick={() => {
              secondary().onClick();
            }}
          >
            {secondary().label}
          </Button>
        )}
      </Show>
      <TryAnotherAccount />
    </div>
  );

  // The CTA targeted by the icon: the primary action if present, otherwise the
  // secondary. It lets tap/click on the illustration trigger the same
  // navigation/retry as the button (no dead end).
  const iconCta = (): { label: string; onClick: () => void } | undefined =>
    props.primaryAction ?? props.secondaryAction;

  return (
    <EmptyState
      icon={ICONS[variant()]}
      title={props.title ?? defaultTitle()}
      description={props.description ?? defaultDescription()}
      action={iconCta() ? action() : undefined}
      onIconActivate={iconCta() ? () => iconCta()?.onClick() : undefined}
      iconActionLabel={iconCta()?.label}
      class={props.class}
    />
  );
};
